import { DocumentTypes } from "@/prisma-package/src";
import { DocumentTypeDetection } from "@/app/_lib/utils/document-type-detection.utils";
import {
  DetectionStatus,
  detectionStatusForScore,
} from "@/app/_lib/utils/document-type-health.utils";
import { POSSIBLE_SCORE } from "@/app/_lib/utils/corpus-validation.utils";

// The per-document recognition verdict shown in the upload verification
// table — same vocabulary as the doc-type training pages.
export type PendingDetection = {
  status: DetectionStatus;
  score: number;
  // The best-scoring candidate type. For a claimed type this is that type;
  // for an OTHER fallback it names the near miss (null when nothing scored),
  // so the verification table can offer it as a one-click correction.
  bestType: string | null;
};

/**
 * Collapses a full detection result to the status pill for one uploaded
 * document. A document that landed on a real type reads its own score; one
 * that fell to OTHER reads its best non-excluded candidate but is clamped
 * below "recognized" — detection declined to claim it, so at best it is
 * "close" to some type.
 */
export const detectionForPending = (
  detection: DocumentTypeDetection
): PendingDetection => {
  if (detection.type !== DocumentTypes.OTHER) {
    const result = detection.results.find(
      (entry) => entry.type === detection.type
    );
    const score = result && !result.excluded ? result.score : 0;
    return {
      status: detectionStatusForScore(score, result?.excluded),
      score,
      bestType: detection.type,
    };
  }

  const best = detection.results
    .filter((entry) => entry.type !== DocumentTypes.OTHER && !entry.excluded)
    .reduce<{ type: string | null; score: number }>(
      (top, entry) => (entry.score > top.score ? entry : top),
      { type: null, score: 0 }
    );
  return {
    status: best.score >= POSSIBLE_SCORE ? "close" : "notRecognized",
    score: best.score,
    bestType: best.score > 0 ? best.type : null,
  };
};

// Detection provenance persisted on the Document at save time.
export type DetectionSaveFields = {
  // "detected" = the saved type is exactly what detection chose; "user" = a
  // person picked it (an explicit upload type, or a correction in the
  // verification modal). User-set labels are trustworthy calibration signal.
  typeSource: "detected" | "user";
  detectedType: string | null;
  detectionScore: number | null;
};

/**
 * The detection fields to persist for one uploaded document. `detectedAs` is
 * the type detection assigned at upload (null when an explicit upload type
 * skipped detection); `savedType` is the type actually being saved after any
 * user edits.
 */
export const detectionFieldsForSave = (
  detection: PendingDetection | null,
  detectedAs: string | null,
  savedType: string
): DetectionSaveFields => {
  if (!detection) {
    return { typeSource: "user", detectedType: null, detectionScore: null };
  }
  return {
    typeSource: savedType === detectedAs ? "detected" : "user",
    detectedType: detection.bestType,
    detectionScore: detection.score,
  };
};
